import React, { useEffect, useMemo, useState } from "react";
import { avanzoApi } from "@/lib/avanzoApi";
import AvanzoTrainV2 from "./AvanzoTrainV2";

/**
 * /avanzo/practica-v2 — Fase 5.
 * Permite elegir materia + habilidad + cantidad (5 o 10) y arranca la
 * misma sesión adaptativa que TRAIN v2, pero en `mode=practice`
 * (5/10 ítems, todos en etapa variable_practice, con anti-repetición
 * y diversidad respetadas).
 */
export default function AvanzoPracticeV2() {
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(null);
  const [habilidades, setHabilidades] = useState([]);
  const [habilidad, setHabilidad] = useState(null);
  const [count, setCount] = useState(5);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    avanzoApi.paesCatalogSubjects()
      .then((r) => setSubjects(r.data.subjects || []))
      .catch(() => setError("No pudimos cargar las materias."));
  }, []);

  useEffect(() => {
    if (!subject) { setHabilidades([]); setHabilidad(null); return; }
    avanzoApi.paesCatalogHabilidades(subject)
      .then((r) => setHabilidades(r.data.habilidades || []))
      .catch(() => setError("No pudimos cargar las habilidades."));
  }, [subject]);
  const practiceParams = useMemo(
    () => ({ subject, habilidad, item_count: count }),
    [subject, habilidad, count],
  );


  if (started) {
    return (
      <div data-testid="practice-active">
        <div className="max-w-3xl mx-auto pt-3 px-4">
          <button
            onClick={() => setStarted(false)}
            className="text-sm text-slate-500 underline"
            data-testid="practice-cancel-btn"
          >
            ← Cambiar mi elección
          </button>
        </div>
        <AvanzoTrainV2
          practiceMode
          practiceParams={practiceParams}
        />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="practice-picker">
      <h1 className="text-2xl font-semibold mb-2">Práctica libre</h1>
      <p className="text-slate-600 mb-6">
        Elige lo que quieras trabajar. La práctica cuenta como constancia,
        no como un examen.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700"
             data-testid="practice-error">{error}</div>
      )}

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-2">Materia</label>
          <div className="grid grid-cols-2 gap-2" data-testid="practice-subjects">
            {subjects.map((s) => (
              <button
                key={s.materia}
                onClick={() => setSubject(s.materia)}
                className={`p-3 rounded border text-left ${
                  subject === s.materia
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200"
                }`}
                data-testid={`practice-subject-${s.materia}`}
              >
                <div className="font-medium capitalize">{s.materia}</div>
                <div className="text-xs text-slate-500">{s.count} preguntas</div>
              </button>
            ))}
          </div>
        </div>

        {subject && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Habilidad <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <select
              value={habilidad || ""}
              onChange={(e) => setHabilidad(e.target.value || null)}
              className="w-full p-2 border border-slate-300 rounded"
              data-testid="practice-habilidad-select"
            >
              <option value="">Cualquier habilidad</option>
              {habilidades.map((h) => (
                <option key={h.habilidad} value={h.habilidad}>
                  {h.habilidad} ({h.count})
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Cantidad</label>
          <div className="flex gap-3">
            {[5, 10].map((n) => (
              <button
                key={n}
                onClick={() => setCount(n)}
                className={`px-5 py-3 rounded border ${
                  count === n ? "border-slate-900 bg-slate-50" : "border-slate-200"
                }`}
                data-testid={`practice-count-${n}`}
              >
                {n} preguntas
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => subject && setStarted(true)}
          disabled={!subject}
          className="w-full py-3 bg-slate-900 text-white rounded disabled:opacity-40"
          data-testid="practice-start-btn"
        >
          Comenzar práctica
        </button>
      </div>
    </div>
  );
}
